import React, { useMemo } from 'react';

// helpers
import useTranslation from 'hooks/useTranslation';
import { styled } from 'styled-components';
import { colorsTheme } from 'resources/theme/styled/colors';
import { useEntityIntakeFormContext } from '../../EntityIntakeFormContext';

// components
import Funding from '../Funding';
import Account from '../Account';
import BasicInfo from '../BasicInfo';
import EntityType from '../EntityType';
import EntityProfile from '../EntityProfile';
import ServiceProviders from '../ServiceProviders';
import ApplicationStatus from '../../ApplicationStatus';
import AdditionalQuestions from '../AdditionalQuestions';
import CustomersAndProducts from '../CustomersAndProducts';
import AssociatedIndividuals from '../AssociatedIndividuals';
import SupplementaryQuestions from '../SupplementaryQuestions';
import ApplicationReviewComments from '../ApplicationReviewComments';
import {
  AsyncButton,
  Button,
  Card,
  Col,
  Collapse,
  Divider,
  Panel,
  Row,
  SectionIntro,
  Text,
} from '@ui';

const Review = () => {
  const { t } = useTranslation('onboarding');
  const {
    intakeForm,
    steps,
    applicationReview,
    navigateToPreviousStep,
    submitApplicationForReview,
  } = useEntityIntakeFormContext();

  const defaultActiveKey = useMemo(() => {
    if (!steps.length) {
      return [];
    }

    return steps.map((e) => e.key);
  }, [steps]);

  return (
    <>
      {intakeForm ? <ApplicationStatus status={intakeForm.state} /> : null}

      {applicationReview?.fieldIssues.length ? (
        <>
          <SectionIntro
            hideDivider
            titleColor={colorsTheme.colorWhite}
            title={t('intake_form.review_submit.section_title')}
            description={t('intake_form.review_submit.description')}
          />
          <StyledCard>
            <ApplicationReviewComments />
          </StyledCard>
        </>
      ) : null}
      <StyledCollapse
        ghost
        bordered={false}
        expandIconPosition="end"
        defaultActiveKey={defaultActiveKey}
      >
        <Panel
          key="entity_type"
          header={
            <Text
              variant="h4"
              weight="semi-bold"
              color={colorsTheme.colorWhite}
            >
              {t('intake_form.entity_type.title')}
            </Text>
          }
        >
          <EntityType
            disabled
            renderNavigateButtonInTitle
            renderEntityTypeUsingSelect
          />
        </Panel>
        <StyledDivider />
        <Panel
          key="entity_details"
          header={
            <Text
              variant="h4"
              weight="semi-bold"
              color={colorsTheme.colorWhite}
            >
              {t('intake_form.entity_details.title')}
            </Text>
          }
        >
          <BasicInfo disabled renderNavigateButtonInTitle />
          <Divider />
          <EntityProfile disabled renderNavigateButtonInTitle />
          <Divider />
          <CustomersAndProducts disabled renderNavigateButtonInTitle />
        </Panel>
        <StyledDivider />
        <Panel
          key="finances"
          header={
            <Text
              variant="h4"
              weight="semi-bold"
              color={colorsTheme.colorWhite}
            >
              {t('intake_form.finances.title')}
            </Text>
          }
        >
          <Funding disabled renderNavigateButtonInTitle />
          <Divider />
          <Account disabled renderNavigateButtonInTitle />
        </Panel>
        <StyledDivider />
        <Panel
          key="associates"
          header={
            <Text
              variant="h4"
              weight="semi-bold"
              color={colorsTheme.colorWhite}
            >
              {t('intake_form.associates.title')}
            </Text>
          }
        >
          <AssociatedIndividuals disabled renderNavigateButtonInTitle />
          <Divider />
          <ServiceProviders disabled renderNavigateButtonInTitle />
        </Panel>
        <StyledDivider />
        {intakeForm?.additionalQuestions &&
        intakeForm.additionalQuestions.length ? (
          <>
            <Panel
              key="additional_questions"
              header={
                <Text
                  variant="h4"
                  weight="semi-bold"
                  color={colorsTheme.colorWhite}
                >
                  {t('intake_form.additional_questions.title')}
                </Text>
              }
            >
              <AdditionalQuestions disabled renderNavigateButtonInTitle />
            </Panel>
            <StyledDivider />
          </>
        ) : null}
        {intakeForm?.triggeredSupplementaryQuestionForms?.length ? (
          <>
            <Panel
              key="supplementary_questions"
              header={
                <Text
                  variant="h4"
                  weight="semi-bold"
                  color={colorsTheme.colorWhite}
                >
                  {t('intake_form.supplementary_questions.title')}
                </Text>
              }
            >
              {intakeForm?.triggeredSupplementaryQuestionForms.map((e) => (
                <SupplementaryQuestions
                  key={e._id}
                  formId={e._id}
                  disabled
                  renderNavigateButtonInTitle
                />
              ))}
            </Panel>
            <StyledDivider />
          </>
        ) : null}
      </StyledCollapse>
      <StyledRow gutter={[16, 16]}>
        <Col>
          <Button size="large" type="bordered" onClick={navigateToPreviousStep}>
            {t('intake_form.submit_buttons.back')}
          </Button>
        </Col>
        {intakeForm?.state === 'draft' ? (
          <Col>
            <AsyncButton size="large" onClick={submitApplicationForReview}>
              {t('intake_form.submit_buttons.submit_for_review')}
            </AsyncButton>
          </Col>
        ) : null}
      </StyledRow>
    </>
  );
};

const StyledCollapse = styled(Collapse)`
  border-block-start: none;

  .ant-collapse-header {
    align-items: center !important;
    padding: ${({ theme }) =>
      `${theme.paddingXl} ${theme.paddingMd} ${theme.paddingXl} 0`} !important;
  }

  .ant-collapse-content-box {
    padding-left: 0px !important;
    padding-top: 0px !important;
  }
`;

const StyledDivider = styled(Divider)`
  margin: 0px;
`;

const StyledRow = styled(Row)`
  margin-top: ${({ theme }) => theme.marginSm};
`;

const StyledCard = styled(Card)`
  .ant-card-body {
    min-height: 0px;
    padding: ${({ theme }) => theme.paddingXl};
  }
`;

export default Review;
